import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { AppConstans } from '../../appConstants';
import { CreateResultDto } from '../../dto/create-result-dto';
import { DrevinaItemDto } from '../../dto/DrevinaItemDto';
import { FormInfoByCisloJrDto } from '../../dto/FormInfoByCisloJrDto';
import { ResultInfoGenericDto } from '../../dto/ResultInfoDto';
import { PuvodUjDto } from '../../dto/Uj/PuvodUjDto';
import { PopInfoDto } from '../../dto/Validators/PopInfoDto';
import { PopValidatorDto } from '../../dto/Validators/PopValidatorDto';
import { SelectItem } from '../../interfaces/select-item';
import { CiselnikyService } from '../../services/ciselniky.service';
import { Erma2Service } from '../../services/erma2.service';
import { FileDownloadService } from '../../services/file-download.service';
import { FillFormService } from '../../services/fill-form.service';
import { PdsMessageService } from '../../Services/pds-message.service';
import { ZadostPopService } from '../../services/zadost-pop.service';
import { CiselnikyUtils } from '../../Utils/ciselniky.utils';
import { FormTouchUtil } from '../../Utils/form-touch-util';
import { UjParseUtils } from '../../Utils/uj.parse.utils';
import { CjrMaLicenciAsyncValidator } from '../../validators/cjr.ma.licenci.async.validator';
import { DruhOstatnihoRmPopOddilyValidator } from '../../validators/druh.ostatniho.rm.pop.oddily.validator';
import { MnozstviOstValidator } from '../../validators/mnozstvi.ost.validator';
import { MnozstviSemValidator } from '../../validators/mnozstvi.sem.validator';
import { PopAsyncValidator } from '../../validators/pop.async.validator';
import { SlouceniOddiluDleUjValidator } from '../../validators/slouceni.oddilu.dle.uj.validator';
import { SlouceniOddiluDodavatelValidator } from '../../validators/slouceni.oddilu.dodavatel.validator';
import { SlouceniOddiluDruhRmValidator } from '../../validators/slouceni.oddilu.druh.rm.validator';
import { VekAZpusobPestovaniRequiredValidator } from '../../validators/vek.a.zpusob.pestovani.required.validator';
import { FormBase } from '../form-base';

@Component({
  selector: 'app-form-zadost-pop-oddily',
  templateUrl: './form-zadost-pop-oddily.component.html',
  styleUrls: ['./form-zadost-pop-oddily.component.css'],
  standalone: false
})
export class FormZadostPopOddilyComponent extends FormBase implements OnInit {

  get slucovaneOddily(): UntypedFormArray {
    return (this.form.get('slucovaneOddily') as UntypedFormArray);
  }

  kategorieRMList: SelectItem[];
  druhRMList: SelectItem[];
  typZdrojeRMList: SelectItem[];
  pobockaList: SelectItem[];
  puvodList: SelectItem[];
  ucelPouzitiList: SelectItem[];
  druhRostlinList: SelectItem[];
  vyskovePasmoList: SelectItem[];
  provenienceList: SelectItem[];
  druhDrevinyList: DrevinaItemDto[];
  popInfos: PopInfoDto[] = [];

  private _neautochtonniNeindigenniKod: string = 'NAN';
  private _semennyRm: number = 1;
  private _sadebniRm: number = 2;
  private _componentName: string = 'FormZadostPopOddilyComponent';
  private _initZadostId: number;
  private _initToken: string;

  override readonly MSG_KEY: string = 'oddily-messages';

  constructor(
    private fb: UntypedFormBuilder,
    private zadostPopService: ZadostPopService,
    private ciselnikyService: CiselnikyService,
    private fillFormService: FillFormService,
    private ujParseUtils: UjParseUtils,
    private titleService: Title,
    private popAsyncValidator: PopAsyncValidator,
    private vekAZpPestovaniValidator: VekAZpusobPestovaniRequiredValidator,
    private licenceValidator: CjrMaLicenciAsyncValidator,
    private ciselnikyUtils: CiselnikyUtils,
    private druhOstatnichRmValidator: DruhOstatnihoRmPopOddilyValidator,
    downloadService: FileDownloadService,
    formTouch: FormTouchUtil,
    protected pdsMessageService: PdsMessageService,
    erma2Service: Erma2Service,
    private route: ActivatedRoute
  ) {
    super(downloadService, formTouch, pdsMessageService, erma2Service);

    this.popAsyncValidator.popInfoEvent.subscribe((resp: PopValidatorDto) => {
      if (!this.validationBeforeSubmit) this.completeByCisloPop(resp);
    });

    this.licenceValidator.licenceEvent.subscribe((resp: FormInfoByCisloJrDto) => {
      if (!this.validationBeforeSubmit) this.completeByCisloJr(resp);
    });

    this.form = this.fb.group({
      cislo: [''],
      rok: [new Date().getFullYear(), Validators.required],
      pobockaPovereneOsoby: [null, Validators.required],
      dodavatel: this.fb.group({
        jmeno: [''],
        prijmeni: [''],
        datNaroz: [new Date()],
        obchNaz: [''],
        adresaTrvalehoPobytuFo: [''],
        adresaPodnikaniFo: [''],
        adresaSidlaPo: [''],
        ico: [''],
      }),
      licence: this.fb.group({
        cj: [''],
        zeDne: [''],
        nabylaPravniMoci: [''],
        cisloJr: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.licenceValidator] }]
      }),
      slucovaneOddily: this.fb.array([this.createOddil()], {
        validators: [
          SlouceniOddiluDleUjValidator(this.ujParseUtils),
          SlouceniOddiluDruhRmValidator(this.popInfos),
          SlouceniOddiluDodavatelValidator(this.popInfos)
        ]
      }),
      oblastProvenience: ['', Validators.required],
      druhDreviny: this.fb.group({
        nazevCesky: ['', Validators.required],
        nazevVedecky: ['', Validators.required],
        oznaceniKlonu: ['']
      }),
      repMaterialKategorie: ['', Validators.required],
      druhRepMaterialu: ['', Validators.required],
      typZdrojeRepMaterialu: ['', Validators.required],
      puvod: [''],
      puvodRm: [''],
      vyskovePasmo: ['', Validators.required],
      genModifikovanyMaterial: false,
      ucelPouziti: [null, Validators.required],
      rokZrani: [''],
      zpusobPestovani: ['', { updateOn: 'blur', validators: [this.vekAZpPestovaniValidator] }],
      seznamPriloh: [''],   // TODO - co s tímhle, má se hodnota ukládat do DB?
      misto: ['', Validators.required],
      datum: [new Date().toISOString().substring(0, 10)]
    });
  } // constructor

  callService(): Observable<ResultInfoGenericDto<CreateResultDto>> {
    return this.zadostPopService.createNew(this.form.value);
  }

  ngOnInit(): void {

    this.route.params.subscribe(params => {
      this._initZadostId = params.zadostId;
      this._initToken = params.token;
      this.setCiselnikyAndData();
    });

    this.titleService.setTitle("ERMA2 - Žádost o potvrzení o původu");
    this.fillFormService.getPrihlasenyUzivatelInfo().subscribe((resp: ResultInfoGenericDto<FormInfoByCisloJrDto>) => {
      if (resp != void 0 && resp.success) {
        this._setDodavatelData(resp.data);
        this._setCjr(resp?.data?.cisloJr);
      }
    });

    this.druhRepMaterialu?.valueChanges.subscribe(_ => {
      if (!this.validationBeforeSubmit) this._druhRmChangeHandler();
    });
  }


  get druhRepMaterialu(): AbstractControl {
    return this.form.get('druhRepMaterialu');
  }

  get isSemRm(): boolean {
    return this.druhRepMaterialu?.value?.kod == this._semennyRm;
  }

  get isSadRm(): boolean {
    return this.druhRepMaterialu?.value?.kod == this._sadebniRm;
  }

  get druhRmHasValue(): boolean {
    return this.druhRepMaterialu?.value != void 0 && this.druhRepMaterialu?.value != '';
  }

  /**
   * Přidání nového oddílu POP.
  **/
  addOddil() {
    this.slucovaneOddily.push(this.createOddil());
  }

  /**
   * Odebrání oddílu. Dle potřeby aktualizuje data, která se načítají z UJ/POP.
   * @param i
   */
  removeOddil(i: number): void {
    this._removePopInfo(this.slucovaneOddily.at(i)?.get('cisloPop')?.value);

    if (i == 0) {
      if (this.slucovaneOddily.length == 1) {
        this.resetOddil(this.slucovaneOddily.at(0) as UntypedFormGroup);
        this.slucovaneOddily.at(0)?.get('cisloPop')?.reset();
        this.resetFieldsFilledByCisloUj();
        this.druhRepMaterialu?.reset();
      }
      else {
        this.slucovaneOddily.removeAt(i);
        let firstOddil = this.slucovaneOddily.at(0) as UntypedFormGroup;

        this.completeByEvCisloUj(firstOddil);

        this.fillFormService.getPopInfo(firstOddil.get('cisloPop')?.value).subscribe(resp => {
          let druhRepMat = null;
          if (resp != void 0 && resp.data != null) {
            druhRepMat = resp.data.druhRepMaterialu;
          }
          this.form.patchValue({
            DruhRepMaterialu: druhRepMat != void 0 ? this.ciselnikyUtils.SelectItemToCiselik(this.druhRMList.find(x => x.value == druhRepMat)) : null,
          });
        });
      }
    }
    else {
      this.slucovaneOddily.removeAt(i);
    }

    this.updateTypZdrojeRepMaterialu();
    this._setRokyZrani();
  }


  /**
   * Vytvoří nový oddíl POP.
  **/
  createOddil(): UntypedFormGroup {
    return this.fb.group({
      cisloPop: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.popAsyncValidator] }],
      zeDne: [''],
      vystavenemu: [''],
      mnozstviMaterialu: ['', Validators.required],
      mnozstviMaterialuSem: ['', { updateOn: 'blur', validators: [MnozstviSemValidator(this._componentName, this._semennyRm)] }],
      mnozstviMaterialuOst: ['', { updateOn: 'blur', validators: [MnozstviOstValidator(this._componentName, this._semennyRm)] }],
      druhRostlin: ['', { updateOn: 'blur', validators: [this.druhOstatnichRmValidator] }],
      evCisloUj: ['']
    });
  }

  /**
  * Načte číselníky ze serveru a naplní formulář
  * */
  setCiselnikyAndData() {
    this.ciselnikyService.getAll().subscribe(lists => {
      this.kategorieRMList = lists.kategorieRm;
      this.druhRMList = lists.druhRm;
      this.typZdrojeRMList = lists.typZdroje;
      this.pobockaList = lists.pobocka.filter(x => x.value != AppConstans.popVydalKodMze);
      this.provenienceList = lists.oblastProvenience;
      this.puvodList = lists.puvod;
      this.ucelPouzitiList = lists.ucelPouziti;
      this.druhRostlinList = lists.druhRostlin;
      this.vyskovePasmoList = lists.vyskovePasmo;
      this.druhDrevinyList = lists.druhDreviny;
      this._loadInitData()
    });
  }

  /**
   * Pokud je zname id zadosti a token, tak nacist data
   * */
  private _loadInitData(): void {
    if (this._initZadostId != void 0 && this._initToken != void 0) {
      this.zadostPopService.get(this._initZadostId, this._initToken).subscribe(res => {
        if (res.success) {
          this.form.patchValue(res.data);
          //TODO - dodělat naplnění množství ve všech oddílech
        } else {
          this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: res.messages[0] })
        }
      });
    }
  }


  /**
   * Dle čísla jednotného registru doplní část údajů o licenci a dodavateli.
  **/
  completeByCisloJr(data: FormInfoByCisloJrDto) {
    this._resetDodavatelData();
    if (data != void 0) {
      this._setDodavatelData(data);
    }
  }


  /**
   * Naplní data dodavatele a licence.
   * @param data
   */
  private _setDodavatelData(data: FormInfoByCisloJrDto) {
    this.form.get('dodavatel')?.patchValue(data);
    this.form.get('licence')?.get('cj').setValue(data?.licence?.cj);
    this.form.get('licence')?.get('nabylaPravniMoci').setValue(data?.licence?.nabylaPravniMoci);
    this.form.get('licence')?.get('zeDne').setValue(data?.licence?.zeDne);
  }


  /**
   * Naplní/vyprázdní číslo jednotného registru.
   * @param cjr
   */
  private _setCjr(cjr: string): void {
    this.form.get('licence')?.get('cisloJr').setValue(cjr);
  }


  /**
   * Reset dat dodavatele a licence.
  **/
  private _resetDodavatelData(): void {
    this.form.get('dodavatel')?.reset();
    this.form.get('licence')?.patchValue({
      cj: '',
      zeDne: '',
      nabylaPravniMoci: ''
    });
  }


  /**
   * Po vyplnění čísla POP vyplní relevantní pole příslušného oddílu.
   * @param popInfo
   */
  private completeByCisloPop(popInfo: PopValidatorDto) {
    let idx: number = (popInfo.control.parent.parent as UntypedFormArray).controls.findIndex((g: UntypedFormGroup) => g.controls.cisloPop?.value == popInfo.control?.value);
    let oddil: UntypedFormGroup = popInfo.control.parent as UntypedFormGroup;

    if (popInfo.data != void 0) {
      this._removePopInfo(popInfo.popInfo?.cisloPop);
      this.popInfos.push(popInfo.popInfo);

      oddil.patchValue({
        zeDne: popInfo.data?.zeDne,
        vystavenemu: popInfo.data?.vystavenemu,
        evCisloUj: popInfo.data?.evCisloUj
      });

      if (idx == 0) {
        this.completeByEvCisloUj(oddil);

        this.form.patchValue({
          druhRepMaterialu: this.ciselnikyUtils.SelectItemToCiselik(this.druhRMList.find(x => x.value == popInfo.data?.druhRepMaterialu)),
        });
      }
    }
    else {
      this.resetOddil(oddil);
      if (idx == 0) {
        this.resetFieldsFilledByCisloUj();
        this.druhRepMaterialu?.reset();
      }
    }

    this.updateTypZdrojeRepMaterialu();
    this._setRokyZrani();
  }


  /**
   * Po vyplnění čísla UJ v prvním oddílu formuláře doplní relevantní pole.
   * @param oddil
   */
  completeByEvCisloUj(oddil: UntypedFormGroup) {
    let cuj = oddil.get('evCisloUj')?.value;
    let info = this.ujParseUtils.parseUj(cuj);

    if (info.isValidCzCisloUj) {
      this.fillFormService.getPuvodUj(cuj).subscribe((resp: PuvodUjDto) => {

        let puvodRm = '';
        if (resp?.puvodKod == this._neautochtonniNeindigenniKod && resp?.puvodPlo != void 0) {
          let pPlo = this.provenienceList.find(x => x.value == resp?.puvodPlo);
          puvodRm = pPlo?.value + ' - ' + pPlo?.label;
        }

        this.form.patchValue({
          puvod: this.ciselnikyUtils.SelectItemToCiselik(this.puvodList.find(x => x.value == resp?.puvodKod)),
          puvodRm: puvodRm
        });
      });

      let druhDreviny = info.isValidCzCisloUj ? this.druhDrevinyList.find(x => x.zkratka == info.druhDreviny) : { nazevCesky: '', nazevVedecky: '' };

      this.form.patchValue({
        oblastProvenience: this.ciselnikyUtils.SelectItemToCiselik(this.provenienceList.find(x => x.value == info.oblastProvenience)),
        repMaterialKategorie: this.ciselnikyUtils.SelectItemToCiselik(this.kategorieRMList.find(x => x.value == info.repMaterialKategorie)),
        vyskovePasmo: this.ciselnikyUtils.SelectItemToCiselik(this.vyskovePasmoList.find(x => x.value == info.vyskovePasmo)),
        druhDreviny: {
          nazevCesky: druhDreviny.nazevCesky,
          nazevVedecky: druhDreviny.nazevVedecky,
        },
      });
    }
  }


  /**
   * Vyčistí příslušnou sekci oddílu, pokud zadané číslo POP není nalezeno
   * @param oddil
   */
  resetOddil(oddil: UntypedFormGroup) {
    Object.keys(oddil.controls).forEach(key => {
      if (key != 'cisloPop') oddil.get(key)?.reset();
    });
  }

  /**
   * Vymaže údaje načítané z UJ.
  **/
  resetFieldsFilledByCisloUj() {
    this.form.patchValue({
      oblastProvenience: '',
      repMaterialKategorie: '',
      typZdrojeRepMaterialu: '',
      vyskovePasmo: '',
      puvod: '',
      puvodRm: ''
    });
    this.form.get('druhDreviny').reset();
  }


  /**
   * Nastaví hodnotu typu zdroje RM dle nejnižšího (nejhoršího) typu zdroje v UJ slučovaných oddílů.
  **/
  updateTypZdrojeRepMaterialu() {
    var typZdrojeArray: string[] = [];

    this.slucovaneOddily.controls.forEach((item, index) => {
      if (item.value.evCisloUj != '') {
        let info = this.ujParseUtils.parseUj(item.value.evCisloUj);
        typZdrojeArray.push(info.typZdrojeRepMaterialu);
      }
    });

    let newValue = typZdrojeArray.sort()[0];
    this.form.patchValue({
      typZdrojeRepMaterialu: this.ciselnikyUtils.SelectItemToCiselik(this.typZdrojeRMList.find(x => x.value == newValue)),
    });
  }

  /**
   * Přetížení tisku formuláře pro zobrazení specifických chyb.
  **/
  onSubmit(isIsds: boolean = false) {
    this._updateMnozstvi();
    this._showErrorMessages();

    super.onSubmit(isIsds);
  }


  /**
   * Aktualizace/překlopení množství do správné položky dle druhu RM.
  **/
  private _updateMnozstvi(): void {
    let druhRm = this.druhRepMaterialu?.value;

    if (druhRm != void 0 && druhRm != '') {
      this.slucovaneOddily.controls.forEach((oddil: UntypedFormGroup) => {
        let mnozstvi;
        if (druhRm.kod == this._semennyRm) {
          mnozstvi = oddil.get('mnozstviMaterialuSem')?.value;
        }
        else {
          mnozstvi = oddil.get('mnozstviMaterialuOst')?.value;
        }

        if (mnozstvi != void 0) {
          oddil.get('mnozstviMaterialu')?.setValue(mnozstvi);
        }
      });
    }
  }


  /**
   * Zobrazení chyb z validace slučování oddílů.
  **/
  private _showErrorMessages(): void {
    this.pdsMessageService.clear(this.MSG_KEY);

    this.messageServiceFormError(this.MSG_KEY);

    if (!this.slucovaneOddily.valid && this.slucovaneOddily.errors != void 0) {
      Object.keys(this.slucovaneOddily.errors).forEach(key => {
        this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: this.slucovaneOddily.errors[key] });
      });
    }
  }


  /**
   * Reset hodnoty ve všech oddílech.
   * @param key klíč hodnoty k vymazání
   */
  private _resetItemInOddily(key: string): void {
    this.slucovaneOddily.controls.forEach((oddil: UntypedFormGroup) => {
      oddil.get(key)?.reset();
    });
  }


  /**
   * Výkonná fce změny druhu RM.
  **/
  private _druhRmChangeHandler(): void {
    if (this.isSemRm) {
      this.form.get('zpusobPestovani')?.reset();
      this._resetItemInOddily('druhRostlin');
      this._resetItemInOddily('mnozstviMaterialuOst');
    }
    else if (this.isSadRm) {
      this.form.get('rokZrani')?.reset();
      this._resetItemInOddily('mnozstviMaterialuSem');
    }
    else {
      this.form.get('rokZrani')?.reset();
      this.form.get('zpusobPestovani')?.reset();
      this._resetItemInOddily('mnozstviMaterialuSem');
    }

    this.slucovaneOddily.controls
      .forEach((oddil: UntypedFormGroup) => {
        this.revalidateControl(oddil.get('druhRostlin'), false);
        this.revalidateControl(oddil.get('mnozstviMaterialuSem'), false);
        this.revalidateControl(oddil.get('mnozstviMaterialuOst'), false);
      });
  }


  /**
   * Odebere info o pop z pomocné kolekce.
   * @param cisloPop
   */
  private _removePopInfo(cisloPop: string): void {
    let popIidx = this.popInfos.findIndex(function (v, i, o) { return v.cisloPop == cisloPop });
    if (popIidx != -1) {
      this.popInfos.splice(popIidx, 1);
    }
  }


  /**
   * Odebre info o pop před změnou modelu v případě, že se dané číslo pop v modelu nachází pouze 1x.
   * @param cisloPop
   */
  private _removeBeforeModelChanged(cisloPop: string): void {
    if (cisloPop != '') {
      let oddilyPop: string[] = [];
      this.slucovaneOddily.controls.forEach((oddil: UntypedFormGroup) =>
        oddilyPop.push(oddil.get('cisloPop')?.value)
      );
      let toRemove = oddilyPop.filter(x => x == cisloPop);
      if (toRemove.length == 1) {
        this._removePopInfo(cisloPop);
      }
    }
  }


  /**
   * Nastaví roky zrání dle oddílů a seřadí je.
  **/
  private _setRokyZrani(): void {
    let rokyDupl: number[] = this.popInfos.map(x => x.rokZrani).filter(x => x != void 0);
    let roky: number[] = rokyDupl
      .filter((value, index) => rokyDupl.indexOf(value) == index)
      .sort(function (a, b) { return a - b; });
    let rokyJoined: string = roky.length > 1 ? roky.join(', ') : roky.toString();
    this.form.get('rokZrani').setValue(rokyJoined);
  }


  /**
   * Change handler čísla POP pro vymazání souvisejících položek v případě vymazání pole. Provádí se po změně modelu.
   * @param value
   * @param i
   */
  popChangedHandler(value: string, i: number): void {
    if (i == 0 && value == '' && this.popInfos.length == 0) {//pokud existuje jen jeden oddíl, tak vymazat vše
      this.resetFieldsFilledByCisloUj();
      this.druhRepMaterialu?.reset();
    }

    if (value == '') {
      this.slucovaneOddily?.at(i).reset();
    }
  }


  /**
   * Change handler čísla POP pro vymazání infa o pop před změnou modelu.
   * @param i
   */
  beforPopChangedHandler(i: number): void {
    let cisloPop = this.slucovaneOddily?.at(i).get('cisloPop')?.value;
    this._removeBeforeModelChanged(cisloPop);
  }
}
